import { AnyAction } from 'redux';
import { handleActions, combineActions } from 'redux-actions';

import { deleteAffiliatedClinicAction } from '@reducers/dentist/DentistActionConfig';

import {
  getProfileDetailsAction,

  updateDentistProfileBioAction,
  updateDentistProfileServicesAction,
  updateDentistProfileAction,
  updateDentistEducationAction,
  deleteDentistEducationAction,

  updateClinicProfileBioAction,
  updateClinicProfileServicesAction,
  updateClinicProfileInformationAction,
  updateClinicProfileAction,
  updateClinicContactDetailsAction,
  deleteClinicHoursAction,

  updateLabProfileBioAction,
  updateLabProfileServicesAction,
  updateLabProfileInformationAction,
  updateLabProfileAction,
  deleteLabHoursAction,

  sendProfileVerificationCodeAction,
  verifyProfileCodeAction,

  setVerificationDetailsAction,
} from './ProfileActionConfig';

// reducers
const defaultState = {
  data: {},
  codeValidityInSeconds: 120,
  verification: {
    emailOrMobile: '',
    timestamp: '',
    type: '',
    verification_code: '',
  },
  verification_details: {
    old_email: '',
    new_email: '',
    old_mobile: '',
    new_mobile: '',
    is_single_verification: true,
  },
};

type State = typeof defaultState;

const reducer = handleActions<State, AnyAction>({
  [`${setVerificationDetailsAction}`](state, { payload }: AnyAction) {
    return {
      ...state,
      verification_details: {
        old_email: payload?.old_email,
        new_email: payload?.new_email,
        old_mobile: payload?.old_mobile,
        new_mobile: payload?.new_mobile,
        is_single_verification: payload?.is_single_verification,
      },
    };
  },
  [`${getProfileDetailsAction}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): profile/{id} now answers {data: <profile>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      data: payload,
    };
  },
  [`${combineActions(updateDentistProfileBioAction, updateClinicProfileBioAction, updateLabProfileBioAction)}`](state, { params }: AnyAction) {
    let newData: any = { ...state.data };

    newData = {
      ...newData,
      bio: params?.bio,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${combineActions(updateDentistProfileServicesAction, updateClinicProfileServicesAction, updateLabProfileServicesAction)}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <ClientSettingResource>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    let newData: any = { ...state.data };

    newData = {
      ...newData,
      client_services: payload,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${combineActions(updateClinicProfileInformationAction, updateLabProfileInformationAction)}`](state, { params, payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <ClientSettingResource>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    let newData: any = { ...state.data };
    const newClientSchedules: any = newData?.client_schedules?.value;

    const newSchedules = {
      ...newClientSchedules,
      value: params?.value,
      id: payload?.id,
      key: payload?.key,
    };

    newData = {
      ...newData,
      client_schedules: newSchedules,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${updateLabProfileAction}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <ProfileResource>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    let newData: any = { ...state.data };

    newData = {
      ...newData,
      name: payload?.name,
      email: payload?.email,
      mobile_number: payload?.mobile_number,
      address: payload?.address,
      image: payload?.image,
      client_contact_numbers: payload?.client_contact_numbers,
      client_phone_number: payload?.client_phone_number,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${updateClinicProfileAction}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <ProfileResource>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    let newData: any = { ...state.data };

    newData = {
      ...newData,
      name: payload?.name,
      image: payload?.image,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${updateClinicContactDetailsAction}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <ProfileResource>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    let newData: any = { ...state.data };

    newData = {
      ...newData,
      email: payload?.email,
      mobile_number: payload?.mobile_number,
      address: payload?.address,
      client_contact_numbers: payload?.client_contact_numbers,
      client_phone_number: payload?.client_phone_number,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${updateDentistProfileAction}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <ProfileResource>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      data: payload,
    };
  },
  [`${combineActions(deleteClinicHoursAction, deleteLabHoursAction)}`](state) {
    let newData: any = { ...state.data };

    newData = {
      ...newData,
      client_schedules: null,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${deleteAffiliatedClinicAction}`](state, { params }: AnyAction) {
    let newData: any = { ...state.data };
    const filteredClinics = newData?.clinics.filter((d: any) => d.id !== params?.id);

    newData = {
      ...newData,
      clinics: filteredClinics,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${updateDentistEducationAction}`](state, { payload: rawPayload, params }: AnyAction) {
    // Phase 3 (backend): now answers {data: <education>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    let newData: any = { ...state.data };

    const dentistEducations = [ ...(newData.dentist_educations || []) ];
    if (!params?.id) {
      dentistEducations.push(payload);
    }
    const dentistEdIdx: any = dentistEducations.length > 0
    && dentistEducations.findIndex((d: any) => d.id === params?.id);

    if (dentistEdIdx !== -1) {
      dentistEducations[dentistEdIdx] = payload;
    }

    newData = {
      ...newData,
      dentist_educations: dentistEducations,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${deleteDentistEducationAction}`](state, { params }: AnyAction) {
    let newData: any = { ...state.data };

    const dentistEducations = [ ...(newData.dentist_educations || []) ];

    const newDentistEducations = dentistEducations.filter((d: any) => d.id !== params?.id);

    newData = {
      ...newData,
      dentist_educations: newDentistEducations,
    };

    return {
      ...state,
      data: newData,
    };
  },
  [`${sendProfileVerificationCodeAction}`](state, { payload: rawPayload, params }: AnyAction) {
    // Phase 3 (backend): now answers {data: {codeValidityInSeconds}} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      codeValidityInSeconds: payload.codeValidityInSeconds,
      verification: {
        emailOrMobile: params?.payload_type === 'mobile' ? `+1${params.email_or_mobile}` : params.email_or_mobile,
        timestamp: new Date(),
        type: params?.payload_type,
      } as any,
    };
  },
  [`${verifyProfileCodeAction}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <VerifyProfileCode result>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      codeValidityInSeconds: 0,
      data: {
        ...state.data,
        email: payload?.email,
        mobile_number: payload?.mobile_number,
      },
    };
  },
}, defaultState);

export default reducer;
